import "./TagSearch.scss"

import {
  CButton,
  CDropdown,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CInput,
  CInputGroup,
  CInputGroupText,
  CSpinner
} from "@coreui/react"
import searchBarCross from "assets/icons/searchBarCross.svg"
import searchBarIcon from "assets/icons/searchBarIcon.svg"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FaTimes } from "react-icons/fa"

import CMSTypography, {
  StandardTypographyTypes
} from "../typography/CMSTypography"

function TagSearch({
  keyValuePairs,
  selectedTags,
  setSelectedTags,
  setTagSearchText = () => {},
  keyToDisplayTextMap = {},
  isDropdownOpen: externalIsDropdownOpen,
  setIsDropdownOpen: setExternalIsDropdownOpen,
  selectedKey: externalSelectedKey,
  setSelectedKey: setExternalSelectedKey,
  placeholder = "",
  isTagsLoading,
  valueToDisplayTextFunc = () => null
}) {
  const dropdownRef = useRef(null)

  const [internalIsDropdownOpen, setInternalIsDropDownOpen] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [internalSelectedKey, setInternalSelectedKey] = useState()

  const isDropdownOpen = useMemo(
    () => externalIsDropdownOpen ?? internalIsDropdownOpen,
    [externalIsDropdownOpen, internalIsDropdownOpen]
  )

  const setIsDropDownOpen = useCallback(
    (vals) =>
      setExternalIsDropdownOpen
        ? setExternalIsDropdownOpen(vals)
        : setInternalIsDropDownOpen(vals),
    [setExternalIsDropdownOpen]
  )

  const selectedKeyForSearch = useMemo(
    () => externalSelectedKey ?? internalSelectedKey,
    [externalSelectedKey, internalSelectedKey]
  )

  const setSelectedKeyForSearch = useCallback(
    (key) =>
      setExternalSelectedKey
        ? setExternalSelectedKey(key)
        : setInternalSelectedKey(key),
    [setExternalSelectedKey]
  )

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropDownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [dropdownRef, setIsDropDownOpen])

  // These are the keys filtered out of all the keys based on searchText
  const keys = useMemo(() => {
    return [...new Set(keyValuePairs.map((eachTag) => eachTag.key))].filter(
      (eachKey) =>
        (keyToDisplayTextMap[eachKey] || eachKey)
          .toLowerCase()
          .includes(searchText.toLowerCase())
    )
  }, [keyToDisplayTextMap, keyValuePairs, searchText])

  /**
   * for some selectedKey, here we filter keyValuePairs which matches with selectedKey
   * such that searchText is included with the associated value in reverse sorted order
   */
  const valuesOfSelectedKey = useMemo(() => {
    return selectedKeyForSearch
      ? keyValuePairs
          .filter((eachTag) => eachTag.key === selectedKeyForSearch)
          .filter(
            (eachTag) =>
              eachTag.count !== 0 &&
              `${
                valueToDisplayTextFunc(eachTag.value, eachTag.key) ||
                eachTag.value
              }`
                .toLowerCase()
                .includes(searchText.toLowerCase())
          )
          .sort((aTag, bTag) => {
            if (aTag.count && bTag.count) {
              if (aTag.count > bTag.count) return -1
              if (aTag.count < bTag.count) return 1
            }
            if (aTag.value > bTag.value) return 1
            if (aTag.value < bTag.value) return -1
            return 0
          })
      : []
  }, [keyValuePairs, searchText, selectedKeyForSearch, valueToDisplayTextFunc])

  // if backspace(keyCode = 8) key is pressed then removing the selectedKey
  const handleSearchKeyDown = (event) => {
    if (
      selectedKeyForSearch &&
      event.target.value === "" &&
      event.keyCode === 8
    ) {
      setSelectedKeyForSearch(null)
    }
  }

  const handleTagSelect = (key, value) => {
    const selectedTagsCopy = { ...selectedTags }
    selectedTagsCopy[key] = [...(selectedTagsCopy[key] || []), value]
    setSelectedTags(selectedTagsCopy)
    setSelectedKeyForSearch(null)
    setSearchText("")
    setTagSearchText("")
    setIsDropDownOpen(false)
  }

  const handleRemoveTag = (key, value) => {
    const selectedTagsCopy = { ...selectedTags }
    selectedTagsCopy[key] = selectedTagsCopy[key].filter(
      (eachValue) => eachValue !== value
    )
    if (selectedTagsCopy[key].length === 0) delete selectedTagsCopy[key]
    setSelectedTags(selectedTagsCopy)
  }
  return (
    <React.Fragment>
      <CDropdown innerRef={dropdownRef}>
        <CInputGroup
          className={classNames("border cms-search-bar-border-radius", {
            "cms-focus": isDropdownOpen
          })}
        >
          <CInputGroupText className="bg-white border-0 cms-search-bar-border-radius">
            <img src={searchBarIcon} alt="search-icon" />
          </CInputGroupText>
          {selectedKeyForSearch && (
            <CInputGroupText className="pr-0 bg-white font-weight-bold border-0">
              {`${
                keyToDisplayTextMap[selectedKeyForSearch] ||
                selectedKeyForSearch
              }: `}
            </CInputGroupText>
          )}
          <CInput
            className="height-auto cms-no-focus border-0"
            value={searchText}
            onChange={(e) => {
              if (selectedKeyForSearch) {
                setTagSearchText("")
              } else setTagSearchText(e.target.value)
              setSearchText(e.target.value)
            }}
            placeholder={placeholder}
            onFocus={() => setIsDropDownOpen(true)}
            onKeyDown={handleSearchKeyDown}
          />
          <CButton
            onClick={() => {
              setSearchText("")
              setTagSearchText("")
              setSelectedKeyForSearch(null)
              setIsDropDownOpen(false)
            }}
            className="bg-white cms-search-bar-border-radius border-0 d-flex align-items-center"
          >
            <img src={searchBarCross} alt="search-icon" />
          </CButton>
        </CInputGroup>
        <CDropdownMenu
          className={classNames("w-100", { show: isDropdownOpen })}
        >
          {isTagsLoading ? (
            <CDropdownHeader className="d-flex justify content-center">
              <CSpinner className="my-3" />
            </CDropdownHeader>
          ) : (
            <>
              <CDropdownHeader className="font-weight-bold">
                {selectedKeyForSearch
                  ? keyToDisplayTextMap[selectedKeyForSearch] ||
                    selectedKeyForSearch
                  : "Filters"}
              </CDropdownHeader>
              {/* for rendering "selectedKeyForSearch: value(count)" if selectedKeyForSearch is there 
              otherwise all the keys, out of which we can select "selectedKeyForSearch". */}
              {selectedKeyForSearch
                ? valuesOfSelectedKey.map((eachValueOfSelectedKey) => (
                    <CDropdownItem
                      key={`tag-val-${eachValueOfSelectedKey.value}`}
                      onClick={() =>
                        handleTagSelect(
                          selectedKeyForSearch,
                          eachValueOfSelectedKey.value
                        )
                      }
                      disabled={selectedTags[selectedKeyForSearch]?.find(
                        (eachValue) =>
                          eachValue === eachValueOfSelectedKey.value
                      )}
                    >
                      <span className="font-weight-bold">
                        {`${
                          keyToDisplayTextMap[selectedKeyForSearch] ||
                          selectedKeyForSearch
                        }:`}
                      </span>
                      &nbsp;
                      {`${
                        valueToDisplayTextFunc(
                          eachValueOfSelectedKey.value,
                          selectedKeyForSearch
                        ) || eachValueOfSelectedKey.value
                      }${
                        eachValueOfSelectedKey.count !== undefined
                          ? ` (${eachValueOfSelectedKey.count})`
                          : ""
                      }`}
                    </CDropdownItem>
                  ))
                : keys.map((eachKey) => (
                    <CDropdownItem
                      key={`tag-key-${eachKey}`}
                      onClick={() => {
                        setSelectedKeyForSearch(eachKey)
                        setSearchText("")
                        setTagSearchText("")
                      }}
                    >
                      {keyToDisplayTextMap[eachKey] || eachKey}
                    </CDropdownItem>
                  ))}
            </>
          )}
        </CDropdownMenu>
      </CDropdown>

      {/* For rendering selectedTags(key value pairs) */}
      <div className={classNames("my-2 cms-chip-container")}>
        {Object.entries(selectedTags).map(([eachKey, values]) =>
          values.map((eachValue) => (
            <div
              key={`${eachKey}: ${eachValue}`}
              className="d-flex align-items-center cms-chip-style"
            >
              <p>
                <span className="font-weight-bold">{`${
                  keyToDisplayTextMap[eachKey] || eachKey
                }: `}</span>
                {valueToDisplayTextFunc(eachValue, eachKey) || eachValue}
              </p>
              <FaTimes
                size="15px"
                className="ml-2 cursor-pointer"
                tabIndex={0}
                onClick={() => handleRemoveTag(eachKey, eachValue)}
              />
            </div>
          ))
        )}
        {Object.keys(selectedTags).length > 0 && (
          <div className="d-flex align-items-center ml-1">
            <CMSTypography
              type={StandardTypographyTypes.cardSubHeading}
              onClick={() => setSelectedTags({})}
              clickable
            >
              Clear All
            </CMSTypography>
          </div>
        )}
      </div>
    </React.Fragment>
  )
}

TagSearch.defaultProps = {
  keyValuePairs: [],
  selectedTags: {},
  setSelectedTags: () => {},
  keyToDisplayTextMap: {}
}

TagSearch.propTypes = {
  keyValuePairs: PropTypes.array.isRequired,
  selectedTags: PropTypes.object.isRequired,
  tagSearchText: PropTypes.string,
  setTagSearchText: PropTypes.func,
  setSelectedTags: PropTypes.func.isRequired,
  keyToDisplayTextMap: PropTypes.object,
  isDropdownOpen: PropTypes.bool,
  setIsDropdownOpen: PropTypes.func,
  selectedKey: PropTypes.string,
  setSelectedKey: PropTypes.func,
  isTagsLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  valueToDisplayTextFunc: PropTypes.func
}

export default TagSearch
